/**
 * O Calendário da Season: os dias da temporada e o que acontece em cada um —
 * "dia 5 tem a invasão, dias 3–7 tem o festival, dia 10 o cartógrafo chega".
 *
 * <p>A tela desenha uma season por vez (chips no topo escolhem qual) e delega a
 * grade e o modal aos componentes ao lado. Toda mutação invalida a árvore
 * {@code ['calendario']} — e nada além dela: o calendário referencia o mundo e não
 * o altera, então não há contagem nem listagem de ficha para invalidar.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErroApi } from '../../../api/cliente';
import { TituloDaPagina } from '../../../shell/TituloDaPagina';
import * as calendarioApi from './calendarioApi';
import type { Acontecimento, Calendario, SeasonDetalhe } from './calendarioTipos';
import { ConfigurarSeason } from './ConfigurarSeason';
import { GradeDaSeason } from './GradeDaSeason';
import { ModalDeAcontecimento } from './ModalDeAcontecimento';
import '../mundo.css';
import './calendario.css';

export function MundoCalendario() {
  const fila = useQueryClient();
  const [seasonEscolhida, setSeasonEscolhida] = useState<number | null>(null);
  const [configurando, setConfigurando] = useState(false);
  const [modal, setModal] = useState<{ acontecimento: Acontecimento | null; dia: number } | null>(
    null
  );
  const [erroDoModal, setErroDoModal] = useState<string | null>(null);
  const [erroDaConfig, setErroDaConfig] = useState<string | null>(null);

  const { data: calendario, isLoading } = useQuery<Calendario>({
    queryKey: ['calendario'],
    queryFn: calendarioApi.calendario,
  });

  // A primeira season é a escolhida por padrão — e segue válida se a atual sumir.
  const seasons = calendario?.seasons ?? [];
  const seasonId =
    seasonEscolhida != null && seasons.some((s) => s.id === seasonEscolhida)
      ? seasonEscolhida
      : (seasons[0]?.id ?? null);

  const { data: detalhe } = useQuery<SeasonDetalhe>({
    queryKey: ['calendario', seasonId],
    queryFn: () => calendarioApi.season(seasonId!),
    enabled: seasonId != null,
  });

  // Trocar de season fecha o formulário de configuração — ele fala da anterior.
  useEffect(() => {
    setConfigurando(false);
  }, [seasonId]);

  const invalidar = () => {
    void fila.invalidateQueries({ queryKey: ['calendario'] });
  };

  const salvarAcontecimento = useMutation({
    mutationFn: ({ id, corpo }: { id: number | null; corpo: Record<string, unknown> }) =>
      id == null
        ? calendarioApi.criarAcontecimento(seasonId!, corpo)
        : calendarioApi.salvarAcontecimento(id, corpo),
    onSuccess: () => {
      invalidar();
      setModal(null);
      setErroDoModal(null);
    },
    onError: (e) => setErroDoModal(e instanceof Error ? e.message : 'Falha ao salvar.'),
  });

  const excluirAcontecimento = useMutation({
    mutationFn: (id: number) => calendarioApi.excluirAcontecimento(id),
    onSuccess: () => {
      invalidar();
      setModal(null);
    },
    onError: (e) => setErroDoModal(e instanceof Error ? e.message : 'Falha ao excluir.'),
  });

  const criarSeason = useMutation({
    mutationFn: () =>
      calendarioApi.criarSeason({ nome: `Season ${seasons.length + 1}` }),
    onSuccess: (nova) => {
      invalidar();
      setSeasonEscolhida(nova.id);
    },
  });

  const salvarSeason = useMutation({
    mutationFn: (mudancas: { nome?: string; diasTotal?: number }) =>
      calendarioApi.salvarSeason(seasonId!, mudancas),
    onSuccess: () => {
      invalidar();
      setConfigurando(false);
      setErroDaConfig(null);
    },
    onError: (e) => setErroDaConfig(e instanceof Error ? e.message : 'Falha ao salvar.'),
  });

  const excluirSeason = useMutation({
    mutationFn: (confirmado: boolean) => calendarioApi.excluirSeason(seasonId!, confirmado),
    onSuccess: () => {
      invalidar();
      setConfigurando(false);
      setSeasonEscolhida(null);
    },
    onError: (e) => {
      // 409: a season tem acontecimentos — a confirmação usa a frase do servidor.
      if (e instanceof ErroApi && e.status === 409) {
        if (window.confirm(e.message)) excluirSeason.mutate(true);
        return;
      }
      setErroDaConfig(e instanceof Error ? e.message : 'Falha ao excluir.');
    },
  });

  if (isLoading) return <div className="mundo mu-nada">Carregando…</div>;

  return (
    <div className="mundo">
      <TituloDaPagina
        acoes={
          seasonId != null && (
            <button className="mu-btn ghost" onClick={() => setConfigurando((v) => !v)}>
              ⚙ Configurar
            </button>
          )
        }
      >
        Os dias da temporada e o que acontece em cada um. Clique num dia para marcar um
        acontecimento; arraste os olhos pela linha para ler a semana.
      </TituloDaPagina>

      {/* Chips das seasons + criar. Projetos sem seed nascem sem nenhuma. */}
      <div className="cal-seasons">
        {seasons.map((s) => (
          <button
            key={s.id}
            className={`mu-fchip${s.id === seasonId ? ' on' : ''}`}
            onClick={() => setSeasonEscolhida(s.id)}
          >
            {s.nome}
            <span className="cal-chip-conta">{s.totalAcontecimentos}</span>
          </button>
        ))}
        <button
          className="mu-fchip"
          disabled={criarSeason.isPending}
          onClick={() => criarSeason.mutate()}
        >
          + Nova season
        </button>
      </div>

      {configurando && detalhe && (
        <ConfigurarSeason
          season={detalhe.season}
          podeExcluir={seasons.length > 1}
          salvando={salvarSeason.isPending || excluirSeason.isPending}
          erro={erroDaConfig}
          aoSalvar={(mudancas) => salvarSeason.mutate(mudancas)}
          aoExcluir={() => excluirSeason.mutate(false)}
          aoFechar={() => {
            setConfigurando(false);
            setErroDaConfig(null);
          }}
        />
      )}

      {seasonId == null && (
        <div className="mu-nada">
          Nenhuma season ainda. Crie a primeira para desenhar o calendário.
        </div>
      )}

      {detalhe && (
        <GradeDaSeason
          diasTotal={detalhe.season.diasTotal}
          acontecimentos={detalhe.acontecimentos}
          aoCriarNoDia={(dia) => {
            setErroDoModal(null);
            setModal({ acontecimento: null, dia });
          }}
          aoAbrir={(acontecimento) => {
            setErroDoModal(null);
            setModal({ acontecimento, dia: acontecimento.diaInicio });
          }}
        />
      )}

      {modal && detalhe && (
        <ModalDeAcontecimento
          acontecimento={modal.acontecimento}
          diaInicial={modal.dia}
          diasTotal={detalhe.season.diasTotal}
          salvando={salvarAcontecimento.isPending}
          erro={erroDoModal}
          aoSalvar={(corpo) =>
            salvarAcontecimento.mutate({ id: modal.acontecimento?.id ?? null, corpo })
          }
          aoExcluir={() => {
            if (modal.acontecimento) excluirAcontecimento.mutate(modal.acontecimento.id);
          }}
          aoFechar={() => setModal(null)}
        />
      )}
    </div>
  );
}
